import { useQuery, useQueryClient } from '@tanstack/react-query';
import { GET_CONTEXT_INSIGHTS } from '../../query/context-insights/keys';
import { ContextInsightsResponse } from '../../../models/context-insights/ContextInsightsResponse';
import { getContextInsights } from '../../services/context-insights/ContextInsightsService';
import { ChatType } from '../../../types/monitor/ChatType';
import { PeriodType } from '../../../types/prompt/PeriodType';

export interface ContextInsightsParams {
  projectId: number;
  monitorPeriod: PeriodType;
  chatType: ChatType;
  from: Date | null;
  to: Date | null;
  monitorPromptIds?: number[];
  filterMonitorTypes?: string[];
}

export const useGetContextInsights = (params: ContextInsightsParams | null) => {
  const queryClient = useQueryClient();

  const fromTimestamp = params?.from?.getTime() ?? null;
  const toTimestamp = params?.to?.getTime() ?? null;

  const query = useQuery<ContextInsightsResponse>({
    queryKey: [
      GET_CONTEXT_INSIGHTS,
      params?.projectId,
      params?.chatType,
      params?.monitorPeriod,
      fromTimestamp,
      toTimestamp,
      params?.monitorPromptIds,
      params?.filterMonitorTypes,
    ],
    queryFn: () => {
      if (!params) {
        throw new Error('Params with dates are required');
      }

      return getContextInsights(
        params.projectId,
        params.chatType,
        params.monitorPeriod,
        params.from,
        params.to,
        params.monitorPromptIds,
        params.filterMonitorTypes
      );
    },
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 30,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    enabled:
      !!params &&
      !!params.from &&
      !!params.to &&
      !!params.chatType &&
      !!params.monitorPeriod &&
      !!params.projectId,
  });

  const invalidate = () => {
    queryClient.invalidateQueries({
      queryKey: [GET_CONTEXT_INSIGHTS, params?.projectId],
      refetchType: 'active',
    });
  };

  return {
    contextInsightsResponse: query.data ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
    invalidate,
  };
};
